import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, errorMessage, tokens } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Textarea,
  Toast,
} from '@/components/ui';

/**
 * Soundboard Bots: the recordings, and what each one is for.
 *
 * The operator's side of the product. A client never sees this page — the
 * clips are yours, and there is nothing on a soundboard for them to configure.
 *
 * The roles are the whole interface. A board is playable the moment it has a
 * greeting and a question, and the engine reads roles rather than a running
 * order somebody has to keep in their head.
 */

interface Board {
  id: string;
  name: string;
  code: string;
  status: string;
  client_name: string;
  tenant_id: string;
  service: string | null;
  max_retries: number;
  handoff: string;
  connect_audio: string;
  clips: number;
  playable: boolean;
  /** Whether any enabled Vicidial server on this account has a transfer destination. */
  can_transfer: boolean;
  client_allowed: boolean;
}

interface ClientOption {
  id: string;
  name: string;
}

interface ClipRow {
  id: string;
  role: string;
  position: number;
  label: string | null;
  transcript: string | null;
  duration_ms: number;
  bytes: number;
}

/**
 * The upload ceiling, matched to `MAX_UPLOAD_BYTES` on the server.
 *
 * Checked in the browser as well, because a file larger than the proxy's own
 * limit never reaches the application at all — nginx answers it with an HTML
 * error page, the browser finds no message in it, and the operator is told
 * only "Upload failed" about a file the server would have explained.
 */
const MAX_CLIP_BYTES = 8 * 1024 * 1024;

/**
 * Why a request failed, in words, whoever answered it.
 *
 * A plain `payload?.error?.message ?? 'Upload failed'` is right about our own
 * JSON and silent about everything else — and everything else is exactly what
 * a failed upload hits: a proxy refusing the size, a gateway timeout, a login
 * that expired into a redirect. None of those carry our envelope, so the
 * operator saw a bare "Upload failed" and had nothing to report and nothing
 * to try.
 *
 * So: our message when there is one, and otherwise the status and whatever
 * the body actually said, cut short.
 */
async function failureReason(response: Response): Promise<string> {
  const text = await response.text().catch(() => '');
  try {
    const payload = JSON.parse(text);
    if (payload?.error?.message) {
      const details = (payload.error.details ?? [])
        .map((d: { field?: string; message?: string }) =>
          d.field ? `${d.field}: ${d.message}` : d.message,
        )
        .filter(Boolean)
        .join('; ');
      return details ? `${payload.error.message} — ${details}` : payload.error.message;
    }
  } catch {
    // Not ours. Fall through and report what actually came back.
  }

  if (response.status === 413) {
    return 'The server refused the file as too large before it reached the application. Check client_max_body_size in the nginx config.';
  }
  // Tags stripped: an nginx error page is mostly markup, and the sentence
  // inside it is the part worth reading.
  const body = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return body
    ? `Upload failed (${response.status}): ${body.slice(0, 200)}`
    : `Upload failed (${response.status} ${response.statusText}).`;
}

const ROLE_HELP: Record<string, string> = {
  GREETING: 'The first thing said when the call connects.',
  QUESTION: 'The qualifying question. Without it a board cannot qualify anybody.',
  REBUTTAL: 'Played instead of a plain repeat, in order, when an answer is unclear.',
  REPEAT: '"Sorry, I did not catch that" — used once the rebuttals run out.',
  QUALIFIED: 'On the way to a human.',
  NOT_QUALIFIED: 'A polite goodbye to somebody who does not own their home.',
  GOODBYE: 'The end of any other call.',
};

export default function Soundboards() {
  const [selected, setSelected] = useState<Board | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );

  const boards = useQuery({
    queryKey: ['soundboards'],
    queryFn: () => api.get<{ data: Board[] }>('/admin/soundboards'),
  });

  return (
    <div>
      <PageHeader
        title="Soundboard Bots"
        description="Recordings instead of a voice. No speech synthesis and no model — a call costs what speech recognition costs."
      />

      <NewBoard onToast={setToast} />

      {boards.isPending ? (
        <Loading label="Reading boards…" />
      ) : boards.error ? (
        <ErrorState error={boards.error} retry={() => boards.refetch()} />
      ) : !boards.data?.data.length ? (
        <EmptyState
          title="No soundboards yet"
          description="Create one above, then upload its clips."
        />
      ) : (
        <Card
          title="Boards"
          description="A board is playable once it has a greeting and a question — and can only hand anybody over once its Vicidial server has a Transfer extension or a Closer DID."
        >
          <div className="divide-y divide-line">
            {boards.data.data.map((board) => (
              <button
                key={board.id}
                type="button"
                className="flex w-full items-center justify-between py-3 text-left"
                onClick={() => setSelected(board)}
              >
                <div>
                  <div className="text-sm font-medium">
                    {board.name}{' '}
                    <span className="text-xs text-ink-500">{board.code}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-500">
                    <span>{board.client_name}</span>
                    <span>{board.clips} clip{board.clips === 1 ? '' : 's'}</span>
                    {board.playable ? (
                      <Badge tone="green">playable</Badge>
                    ) : (
                      <Badge tone="red">no question clip</Badge>
                    )}
                    {/* Bigger than "playable". A board that cannot transfer
                        still answers, still screens, still costs a call — and
                        then throws away the one lead it was there to find. */}
                    {!board.can_transfer && (
                      <Badge tone="red">cannot transfer</Badge>
                    )}
                    {board.service && <span>{board.service}</span>}
                    {!board.client_allowed && (
                      <Badge tone="amber">client not enabled</Badge>
                    )}
                  </div>
                </div>
                <span className="text-xs text-ink-500">
                  {selected?.id === board.id ? 'open' : 'clips →'}
                </span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {selected && (
        <>
          <BoardSettings board={selected} onToast={setToast} />
          <BoardEconomics board={selected} />
          <BoardRules board={selected} onToast={setToast} />
          <BoardClips board={selected} onToast={setToast} />
        </>
      )}

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}

/**
 * Making a board.
 *
 * Two things have to happen and somebody will otherwise do one of them: the
 * bot has to exist, and the client has to be allowed to see the product. The
 * second one failing is silent — the board works, the client's page says
 * Soundboard Bots are not on their account, and nothing connects the two — so
 * it is on by default here rather than being a separate errand.
 */
function NewBoard({
  onToast,
}: {
  onToast: (t: { message: string; tone: 'good' | 'bad' }) => void;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [service, setService] = useState('');
  const [enable, setEnable] = useState(true);

  const clients = useQuery({
    queryKey: ['clients', 'options'],
    queryFn: () => api.get<{ data: ClientOption[] }>('/admin/clients', { page_size: 200 }),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post('/admin/soundboards', {
        tenant_id: tenantId,
        name: name.trim(),
        code: code.trim().toUpperCase(),
        service: service.trim() || null,
        enable_for_client: enable,
      }),
    onSuccess: () => {
      onToast({ message: 'Soundboard created. Upload its clips next.', tone: 'good' });
      queryClient.invalidateQueries({ queryKey: ['soundboards'] });
      setName('');
      setCode('');
      setService('');
      setOpen(false);
    },
    onError: (err) => onToast({ message: errorMessage(err, 'Could not create'), tone: 'bad' }),
  });

  if (!open) {
    return (
      <div className="mb-4">
        <Button onClick={() => setOpen(true)}>New soundboard</Button>
      </div>
    );
  }

  const ready = tenantId && name.trim() && code.trim();

  return (
    <Card
      title="New soundboard"
      description="A board plays recordings and listens for a yes. No voice, no prompt, no model to choose."
      className="mb-4"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Client">
          <Select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
            <option value="">Choose a client…</option>
            {(clients.data?.data ?? []).map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Roofing screener"
          />
        </Field>
        <Field label="Code" hint="How the dialer refers to it. Letters, numbers, - and _.">
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="ROOF01" />
        </Field>
        <Field label="Service" hint="Shown to the client so they can tell two boards apart.">
          <Input
            value={service}
            onChange={(e) => setService(e.target.value)}
            placeholder="Roofing"
          />
        </Field>
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input type="checkbox" checked={enable} onChange={(e) => setEnable(e.target.checked)} />
          Turn Soundboard Bots on for this client
        </label>
        <div className="flex gap-2 sm:col-span-2">
          <Button loading={create.isPending} disabled={!ready} onClick={() => create.mutate()}>
            Create
          </Button>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * What a board is for, and whether its client may use it.
 *
 * Everything here is the operator's. The client sees the name, the service and
 * the calls it made, and nothing else — there is nothing on a soundboard for
 * them to configure.
 */
function BoardSettings({
  board,
  onToast,
}: {
  board: Board;
  onToast: (t: { message: string; tone: 'good' | 'bad' }) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(board.name);
  const [service, setService] = useState(board.service ?? '');
  const [retries, setRetries] = useState(board.max_retries);
  const [status, setStatus] = useState(board.status);
  const [handoff, setHandoff] = useState(board.handoff ?? 'WARM');
  const [connectAudio, setConnectAudio] = useState(board.connect_audio ?? 'RINGING');

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['soundboards'] });

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/admin/soundboards/${board.id}`, {
        name: name.trim(),
        service: service.trim() || null,
        max_retries: retries,
        status,
        handoff,
        connect_audio: connectAudio,
      }),
    onSuccess: () => {
      onToast({ message: 'Saved.', tone: 'good' });
      refresh();
    },
    onError: (err) => onToast({ message: errorMessage(err, 'Could not save'), tone: 'bad' }),
  });

  const access = useMutation({
    mutationFn: (enabled: boolean) =>
      api.put(`/admin/clients/${board.tenant_id}/soundboard-access`, { enabled }),
    onSuccess: (_data, enabled) => {
      onToast({
        message: enabled
          ? `${board.client_name} can now see their Soundboard Bots.`
          : `Soundboard Bots hidden from ${board.client_name}.`,
        tone: 'good',
      });
      refresh();
    },
    onError: (err) => onToast({ message: errorMessage(err, 'Could not change'), tone: 'bad' }),
  });

  return (
    <Card title={`${board.name} — settings`} className="mt-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Service">
          <Input value={service} onChange={(e) => setService(e.target.value)} />
        </Field>
        <Field
          label="Retries"
          hint="0 — ask once and stop, which is what a board of recordings usually wants. Higher replays the question at somebody who already heard it."
        >
          <Input
            type="number"
            min={0}
            max={5}
            value={retries}
            onChange={(e) => setRetries(Number(e.target.value))}
          />
        </Field>
        <Field label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="ACTIVE">Active</option>
            <option value="PAUSED">Paused</option>
            <option value="DRAFT">Draft</option>
            <option value="ARCHIVED">Archived</option>
          </Select>
        </Field>
        {/* The shape of the hand-over, not its destination. A board has no
            settings page of its own, and this is the setting most likely to
            be wrong for it: a screener hands over and is finished, so there
            is nothing for it to stay in the call for. */}
        <Field
          label="Hand-over"
          hint="Blind needs a Transfer extension on the Vicidial server. Without one the transfer still happens, warm, and says so in the log."
        >
          <Select value={handoff} onChange={(e) => setHandoff(e.target.value)}>
            <option value="WARM">Warm — Glee dials and bridges</option>
            <option value="BLIND">Blind — hand to the dialplan and leave</option>
          </Select>
        </Field>
        <Field
          label="While the closer is found"
          hint="Warm only. On a blind hand-over the dialplan gives ringback itself."
        >
          <Select
            value={connectAudio}
            onChange={(e) => setConnectAudio(e.target.value)}
          >
            <option value="RINGING">Ringing</option>
            <option value="MUSIC">Music on hold</option>
            <option value="SILENCE">Nothing</option>
          </Select>
        </Field>

        <p className="text-xs text-ink-500 sm:col-span-4">
          A board has nothing to say after the hand-over, so blind usually
          suits it: the channel goes to the dialplan and nothing is held here.
          What you give up is knowing whether a human picked up — a blind
          transfer is counted as connected at the moment it is handed over.
        </p>

        <div className="sm:col-span-4">
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </div>
      </div>

      {/* The one failure worth interrupting somebody for. A board with no
          transfer destination answers, screens, finds a homeowner, and then
          drops them — the call spent, the lead spent, and the one thing this
          platform is paid for not done. Nothing said so until a log was
          read. */}
      {!board.can_transfer && (
        <div className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100">
          <p className="font-medium">This board cannot hand anybody over.</p>
          <p className="mt-1 text-xs leading-relaxed">
            It will answer, ask its question, and find homeowners — and then
            lose every one of them, because no Vicidial server on this account
            has anywhere to send them. Set a <strong>Transfer extension</strong>{' '}
            or a <strong>Closer DID</strong> on{' '}
            <strong>Vicidial servers</strong>, then press <strong>Apply</strong>.
          </p>
          <p className="mt-1 text-xs leading-relaxed">
            A closer in-group on its own is not enough here: these calls arrive
            as <code className="font-mono">VDAD</code>, Vicidial&apos;s
            auto-dial pseudo-user, which is not an agent session — so an
            in-group can never be dialled for them.
          </p>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between border-t border-line pt-4">
        <div>
          <div className="text-sm font-medium">Client access</div>
          <p className="mt-0.5 text-xs text-ink-500">
            {board.client_allowed
              ? `${board.client_name} can see their Soundboard Bots and the calls they made.`
              : `${board.client_name} cannot see this product. The board still runs — they just have no page for it.`}
          </p>
        </div>
        <Button
          variant={board.client_allowed ? 'ghost' : 'primary'}
          loading={access.isPending}
          onClick={() => access.mutate(!board.client_allowed)}
        >
          {board.client_allowed ? 'Turn off' : 'Turn on'}
        </Button>
      </div>
    </Card>
  );
}

interface RulesPayload {
  transfer_phrases: string[];
  reject_phrases: string[];
  using_defaults: boolean;
  defaults: { transfer_phrases: string[]; reject_phrases: string[] };
}

interface TestResult {
  verdict: string;
  matched: string;
  heard_as: string;
  transferred: boolean;
  using_defaults: boolean;
}

/**
 * What this board treats as a yes and as a no — and a box to argue with it.
 *
 * This is the product. Everything else on the page is plumbing around one
 * decision: does this person get handed to a closer. A wrong yes sends a
 * renter to somebody paid by the hour; a wrong no throws away a lead nobody
 * will ever call again.
 *
 * Two things make that decision safe to hand to an operator.
 *
 * The lists REPLACE the built-in ones rather than adding to them, and the
 * screen says so. Adding would be the friendly choice and the wrong one: a
 * solar board would inherit "my landlord" as a rejection, drop good leads for
 * mentioning theirs, and leave nothing in the log that looks wrong.
 *
 * And the tester runs against the SAVED board, so what it answers is what the
 * next live call answers. A tester scoring against the text in the boxes
 * would agree with itself and disagree with the phone line.
 */
function BoardRules({
  board,
  onToast,
}: {
  board: Board;
  onToast: (t: { message: string; tone: 'good' | 'bad' }) => void;
}) {
  const queryClient = useQueryClient();
  const [transfer, setTransfer] = useState<string | null>(null);
  const [reject, setReject] = useState<string | null>(null);
  const [said, setSaid] = useState('');

  const rules = useQuery({
    queryKey: ['soundboard-rules', board.id],
    queryFn: () => api.get<RulesPayload>(`/admin/soundboards/${board.id}/rules`),
  });

  // The boxes show what is ACTUALLY RUNNING, which for an unconfigured board
  // is the homeowner tables. An empty box would imply the board decides
  // nothing, and the operator's first act would be to invent a list that
  // already existed and was better.
  const current = rules.data;
  const transferText =
    transfer ??
    (current
      ? (current.using_defaults
          ? current.defaults.transfer_phrases
          : current.transfer_phrases
        ).join('\n')
      : '');
  const rejectText =
    reject ??
    (current
      ? (current.using_defaults
          ? current.defaults.reject_phrases
          : current.reject_phrases
        ).join('\n')
      : '');

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/admin/soundboards/${board.id}`, {
        transfer_phrases: transferText.split('\n'),
        reject_phrases: rejectText.split('\n'),
      }),
    onSuccess: () => {
      onToast({ message: 'Saved. The next call uses these.', tone: 'good' });
      setTransfer(null);
      setReject(null);
      queryClient.invalidateQueries({ queryKey: ['soundboard-rules', board.id] });
    },
    onError: (err) => onToast({ message: errorMessage(err, 'Could not save'), tone: 'bad' }),
  });

  const tryIt = useMutation({
    mutationFn: () =>
      api.post<TestResult>(`/admin/soundboards/${board.id}/test`, { said: said.trim() }),
    onError: (err) => onToast({ message: errorMessage(err, 'Could not test'), tone: 'bad' }),
  });

  const result = tryIt.data;
  const dirty = transfer !== null || reject !== null;

  return (
    <Card
      title={`${board.name} — what it listens for`}
      description="One phrase per line. A rejection wins wherever it appears, and anything that matches neither list is asked again rather than transferred."
      className="mt-4"
    >
      {rules.isPending ? (
        <Loading label="Reading phrases…" />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Transfer when you hear"
              hint="Short answers to the question — yes, yeah, that's right — always count, on every board."
            >
              <Textarea
                rows={10}
                className="font-mono text-xs"
                value={transferText}
                onChange={(e) => setTransfer(e.target.value)}
              />
            </Field>
            <Field
              label="Never transfer when you hear"
              hint="Checked first, and it wins outright. “Yes, but we're renting” is a renter."
            >
              <Textarea
                rows={10}
                className="font-mono text-xs"
                value={rejectText}
                onChange={(e) => setReject(e.target.value)}
              />
            </Field>
          </div>

          <p className="mt-2 text-xs text-ink-500">
            {current?.using_defaults
              ? 'This board is running the built-in homeowner lists, shown above. Saving makes them its own — after that they replace the built-in ones rather than adding to them.'
              : 'This board uses its own lists. The built-in homeowner phrases are not applied underneath — a board screening for something else should not reject somebody for mentioning their landlord.'}
          </p>

          <div className="mt-3 flex gap-2">
            <Button loading={save.isPending} disabled={!dirty} onClick={() => save.mutate()}>
              Save phrases
            </Button>
            {dirty && (
              <Button
                variant="ghost"
                onClick={() => {
                  setTransfer(null);
                  setReject(null);
                }}
              >
                Discard changes
              </Button>
            )}
          </div>

          {/* The tester. A phrase table is only trustworthy if it can be
              argued with, and the argument is always about one sentence
              somebody actually said. */}
          <div className="mt-4 border-t border-line pt-4">
            <div className="text-sm font-medium">Try a sentence</div>
            <p className="mt-0.5 text-xs text-ink-500">
              Runs against the saved board, so this is what the next live call
              would do. Paste a line out of a transcript.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Input
                className="min-w-[16rem] flex-1"
                value={said}
                placeholder="Yeah, it's my house"
                onChange={(e) => setSaid(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && said.trim()) tryIt.mutate();
                }}
              />
              <Button
                variant="secondary"
                loading={tryIt.isPending}
                disabled={!said.trim()}
                onClick={() => tryIt.mutate()}
              >
                Test
              </Button>
            </div>

            {dirty && (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                You have unsaved changes. The test uses the saved phrases, not
                the ones in the boxes.
              </p>
            )}

            {result && (
              <div className="mt-3 rounded-md border border-line p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    tone={
                      result.verdict === 'QUALIFIED'
                        ? 'green'
                        : result.verdict === 'NOT_QUALIFIED'
                          ? 'red'
                          : 'amber'
                    }
                  >
                    {result.verdict.replace('_', ' ').toLowerCase()}
                  </Badge>
                  <span className="text-xs text-ink-600 dark:text-ink-300">
                    {result.transferred
                      ? 'Transferred to a closer.'
                      : result.verdict === 'NOT_QUALIFIED'
                        ? 'Goodbye clip, and the call ends. Never transferred.'
                        : 'Asked again, up to the retry limit, then the call ends without a transfer.'}
                  </span>
                </div>
                {/* Without this a verdict is an opinion. */}
                {result.matched && (
                  <p className="mt-2 text-xs text-ink-600 dark:text-ink-300">
                    Decided by: <code className="rounded bg-sunken px-1 py-0.5">{result.matched}</code>
                  </p>
                )}
                {/* The answer to "why does my phrase not fire", nine times in ten. */}
                <p className="mt-1 text-xs text-ink-500">
                  Matched against: <code className="rounded bg-sunken px-1 py-0.5">{result.heard_as}</code>
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

function BoardClips({
  board,
  onToast,
}: {
  board: Board;
  onToast: (t: { message: string; tone: 'good' | 'bad' }) => void;
}) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [role, setRole] = useState('GREETING');
  const [position, setPosition] = useState(1);
  const [label, setLabel] = useState('');
  const [uploading, setUploading] = useState(false);

  const clips = useQuery({
    queryKey: ['soundboard-clips', board.id],
    queryFn: () =>
      api.get<{ data: ClipRow[]; missing: string[]; roles: string[] }>(
        `/admin/soundboards/${board.id}/clips`,
      ),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['soundboard-clips', board.id] });

  /**
   * Uploaded with fetch rather than the JSON client: this is multipart, and
   * the client sets a JSON content type that would make the server reject it.
   */
  async function upload() {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      onToast({ message: 'Choose a WAV file first.', tone: 'bad' });
      return;
    }
    // Refused here, before it is sent. Over the proxy's limit the request
    // never reaches the application, and what comes back is an nginx HTML
    // error page with no message in it — which is how a too-large clip
    // became a bare "Upload failed" with no reason anywhere.
    if (file.size > MAX_CLIP_BYTES) {
      onToast({
        message: `${file.name} is ${(file.size / 1048576).toFixed(1)}MB and the limit is ${MAX_CLIP_BYTES / 1048576}MB. Export it as mono, 8000 Hz, 16-bit PCM WAV.`,
        tone: 'bad',
      });
      return;
    }
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const query = new URLSearchParams({
        role,
        position: String(position),
        ...(label ? { label } : {}),
      });
      const response = await fetch(
        `/api/v1/admin/soundboards/${board.id}/clips?${query}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${tokens.access() ?? ''}` },
          body,
        },
      );
      if (!response.ok) {
        throw new Error(await failureReason(response));
      }
      onToast({ message: 'Clip uploaded.', tone: 'good' });
      if (fileRef.current) fileRef.current.value = '';
      setLabel('');
      refresh();
      queryClient.invalidateQueries({ queryKey: ['soundboards'] });
    } catch (err) {
      onToast({ message: errorMessage(err, 'Upload failed'), tone: 'bad' });
    } finally {
      setUploading(false);
    }
  }

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/soundboards/clips/${id}`),
    onSuccess: () => {
      refresh();
      queryClient.invalidateQueries({ queryKey: ['soundboards'] });
    },
    onError: (err) => onToast({ message: errorMessage(err, 'Could not remove'), tone: 'bad' }),
  });

  function play(clipId: string) {
    api
      .getBlob(`/admin/soundboards/clips/${clipId}/audio`)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.onended = () => URL.revokeObjectURL(url);
        return audio.play();
      })
      .catch(() => onToast({ message: 'That clip could not be played.', tone: 'bad' }));
  }

  const missing = clips.data?.missing ?? [];

  return (
    <Card
      title={`${board.name} — clips`}
      description="Generate them from text, or upload 16-bit PCM WAV. Either way the audio is converted once, here, to exactly what the phone line plays."
      className="mt-4"
    >
      {missing.length > 0 && (
        <div className="mb-4 rounded-md bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          This board still needs: {missing.join(', ').toLowerCase()}. Without a
          question clip it cannot qualify anybody, and a call ends as soon as it
          starts.
        </div>
      )}

      <GenerateClip board={board} onToast={onToast} onDone={refresh} />

      <div className="mb-4 grid gap-3 rounded-md border border-line p-3 sm:grid-cols-4">
        <Field label="Role" hint={ROLE_HELP[role]}>
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            {(clips.data?.roles ?? Object.keys(ROLE_HELP)).map((value) => (
              <option key={value} value={value}>
                {value.replace('_', ' ').toLowerCase()}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Order" hint="Two rebuttals play in turn. The first is 1.">
          <Input
            type="number"
            min={1}
            max={50}
            value={position}
            // Clamped here, not left to `min`. A number input's `min` is only
            // enforced by form validation, and this uploads from a click —
            // so clearing the box sent `0`, which the column's CHECK refused
            // and the operator saw as an internal error reference.
            onChange={(e) =>
              setPosition(Math.min(50, Math.max(1, Number(e.target.value) || 1)))
            }
          />
        </Field>
        <Field label="Label">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="what it says"
          />
        </Field>
        <Field
          label="WAV file"
          hint="16-bit PCM, mono or stereo, uncompressed. Up to 8MB."
        >
          <input ref={fileRef} type="file" accept=".wav,audio/wav" className="text-sm" />
        </Field>
        <div className="sm:col-span-4">
          <Button loading={uploading} onClick={upload}>
            Upload clip
          </Button>
        </div>
      </div>

      {clips.isPending ? (
        <Loading label="Reading clips…" />
      ) : !clips.data?.data.length ? (
        <p className="text-sm text-ink-500">Nothing uploaded yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="glee-table glee-table--compact">
            <thead>
              <tr>
                <th>Role</th>
                <th>Label</th>
                <th className="text-right">Length</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {clips.data.data.map((clip) => (
                <tr key={clip.id}>
                  <td>
                    {clip.role.replace('_', ' ').toLowerCase()}
                    {clip.position > 1 && (
                      <span className="ml-1 text-xs text-ink-500">#{clip.position}</span>
                    )}
                  </td>
                  <td>{clip.label ?? '—'}</td>
                  <td className="text-right tabular-nums text-xs text-ink-500">
                    {(clip.duration_ms / 1000).toFixed(1)}s
                  </td>
                  <td>
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => play(clip.id)}>
                        Play
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => remove.mutate(clip.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

interface TtsProvider {
  id: string;
  name: string;
  driver: string;
  is_default: boolean;
  enabled: boolean;
}

interface SavedVoice {
  id: string;
  voice_id: string;
  nickname: string;
  provider_id: string | null;
  provider_driver: string | null;
  mismatched: boolean;
}

/**
 * Making a clip out of a sentence.
 *
 * A board is seven recordings, and until now the only way to get them was to
 * record them: a quiet room, a microphone, somebody whose voice you are
 * willing to put on ten thousand calls, and the whole thing again when the
 * script changes one word.
 *
 * THE VENDOR IS CALLED ONCE, HERE, AND NEVER ON A CALL.
 *
 * That is not an optimisation, it is the product. A Soundboard Bot has no
 * speech synthesis on the phone line — the words are billed when this button
 * is pressed, and every call afterwards plays a file. Seven clips cost about
 * a penny to build and nothing per call thereafter, at any volume. It is
 * worth saying on the screen, because a text-to-speech provider appearing on
 * a product sold as having no speech cost is the obvious thing to be
 * suspicious about.
 */
function GenerateClip({
  board,
  onToast,
  onDone,
}: {
  board: Board;
  onToast: (t: { message: string; tone: 'good' | 'bad' }) => void;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState('GREETING');
  const [position, setPosition] = useState(1);
  const [text, setText] = useState('');
  const [providerId, setProviderId] = useState('');
  const [voiceId, setVoiceId] = useState('');

  const providers = useQuery({
    queryKey: ['providers', 'tts'],
    queryFn: () => api.get<{ data: TtsProvider[] }>('/admin/providers', { kind: 'TTS' }),
    enabled: open,
  });

  // The account's own saved voices, with the names somebody gave them. A
  // voice id is twenty characters of base62 and nobody can tell two apart.
  const voices = useQuery({
    queryKey: ['client-voices', board.tenant_id],
    queryFn: () =>
      api.get<{ data: SavedVoice[] }>(`/admin/clients/${board.tenant_id}/voices`),
    enabled: open,
  });

  const generate = useMutation({
    mutationFn: () =>
      api.post(`/admin/soundboards/${board.id}/clips/generate`, {
        role,
        position,
        text: text.trim(),
        provider_id: providerId || null,
        voice_id: voiceId.trim() || null,
      }),
    onSuccess: () => {
      onToast({ message: 'Clip generated. Play it before you rely on it.', tone: 'good' });
      setText('');
      onDone();
    },
    onError: (err) =>
      onToast({ message: errorMessage(err, 'Could not generate that'), tone: 'bad' }),
  });

  if (!open) {
    return (
      <div className="mb-4">
        <Button variant="secondary" onClick={() => setOpen(true)}>
          Generate a clip from text
        </Button>
      </div>
    );
  }

  const usable = (providers.data?.data ?? []).filter((p) => p.enabled);
  const saved = voices.data?.data ?? [];

  return (
    <div className="mb-4 rounded-md border border-brand-200 bg-brand-50/40 p-3 dark:border-brand-900 dark:bg-brand-950/20">
      <div className="mb-2 text-sm font-medium">Generate a clip</div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Field label="Voice provider" hint="Blank uses the platform default.">
          <Select value={providerId} onChange={(e) => setProviderId(e.target.value)}>
            <option value="">Platform default</option>
            {usable.map((provider) => (
              <option key={provider.id} value={provider.id}>
                {provider.name} ({provider.driver})
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Voice"
          hint={
            saved.length
              ? 'One of this client’s saved voices, or paste an id.'
              : 'Paste a voice id. Blank uses the provider’s own default.'
          }
        >
          {saved.length ? (
            <Select value={voiceId} onChange={(e) => setVoiceId(e.target.value)}>
              <option value="">Provider default</option>
              {saved.map((voice) => (
                <option key={voice.id} value={voice.voice_id}>
                  {voice.nickname}
                  {voice.mismatched ? ' — wrong vendor?' : ''}
                </option>
              ))}
            </Select>
          ) : (
            <Input
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              placeholder="voice id"
            />
          )}
        </Field>

        <Field label="Role" hint={ROLE_HELP[role]}>
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            {Object.keys(ROLE_HELP).map((value) => (
              <option key={value} value={value}>
                {value.replace('_', ' ').toLowerCase()}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Order" hint="Two rebuttals play in turn.">
          <Input
            type="number"
            min={1}
            value={position}
            onChange={(e) => setPosition(Number(e.target.value))}
          />
        </Field>

        <div className="sm:col-span-4">
          <Field label="What it says">
            <Textarea
              rows={2}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Hi, this is Sarah — quick question, are you the homeowner there?"
            />
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:col-span-4">
          <Button
            loading={generate.isPending}
            disabled={!text.trim()}
            onClick={() => generate.mutate()}
          >
            Generate and assign
          </Button>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Close
          </Button>
          <span className="text-xs text-ink-500">
            The voice provider is billed once, now — never on a call. A whole
            board costs about a penny to build and nothing per call after that,
            however many calls it makes.
          </span>
        </div>
      </div>
    </div>
  );
}

interface Economics {
  days: number;
  calls: number;
  answered: number;
  transferred: number;
  talk_minutes: number;
  spent: number;
  recognition: number;
  telephony: number;
  synthesis_and_model: number;
  per_call: number;
  per_transfer: number | null;
  projected_monthly: number;
  monthly_price: number;
  monthly_margin: number;
}

/**
 * What this board actually cost, against what it is actually charged.
 *
 * A Soundboard Bot is sold by the month and costs money by the minute, which
 * is a shape that hides its own losses: every call is a fraction of a cent and
 * the bill arrives thirty days later in one number. Everybody selling this
 * product quotes a per-minute rate; almost nobody knows their own.
 *
 * So the margin is on the same screen as the board, and it is measured rather
 * than estimated. The only projected figure says so in its own label.
 *
 * `synthesis_and_model` should be exactly zero on every board for ever. If it
 * is not, a soundboard call has reached a synthesiser or a language model —
 * the one failure that would turn this product's margin negative silently,
 * because the calls would go on working perfectly.
 */
function BoardEconomics({ board }: { board: Board }) {
  const [price, setPrice] = useState('30');

  const data = useQuery({
    queryKey: ['soundboard-economics', board.id, price],
    queryFn: () =>
      api.get<Economics>(`/admin/soundboards/${board.id}/economics`, {
        days: 30,
        monthly_price: Number(price) || 0,
      }),
  });

  const money = (value: number, places = 2) =>
    `$${value.toFixed(places)}`;

  return (
    <Card
      title={`${board.name} — what it costs`}
      description="Measured from this board's own calls. Speech recognition is the only per-call cost a soundboard has."
      className="mt-4"
    >
      {data.isPending ? (
        <Loading label="Adding it up…" />
      ) : data.error ? (
        <ErrorState error={data.error} retry={() => data.refetch()} />
      ) : !data.data?.calls ? (
        <p className="text-sm text-ink-500">
          No calls in the last 30 days, so there is nothing to add up yet.
        </p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <Stat label="Calls (30 days)" value={data.data.calls.toLocaleString()} />
            <Stat label="Transferred" value={data.data.transferred.toLocaleString()} />
            <Stat label="Cost per call" value={money(data.data.per_call, 4)} />
            <Stat
              label="Cost per transfer"
              value={data.data.per_transfer === null ? '—' : money(data.data.per_transfer, 3)}
            />
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-line pt-4">
            <Field label="Charged per month" hint="What this client pays for the board.">
              <Input
                type="number"
                min={0}
                className="w-28"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </Field>
            <div className="text-sm">
              <div className="text-xs text-ink-500">
                Projected monthly cost at this rate of calling
              </div>
              <div className="tabular-nums">
                {money(data.data.projected_monthly)}{' '}
                <span
                  className={
                    data.data.monthly_margin >= 0
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400'
                  }
                >
                  → {data.data.monthly_margin >= 0 ? 'leaves' : 'loses'}{' '}
                  {money(Math.abs(data.data.monthly_margin))} a month
                </span>
              </div>
            </div>
          </div>

          {/* Zero, on every board, for ever. */}
          {data.data.synthesis_and_model > 0 && (
            <div className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-900 dark:bg-red-950/40 dark:text-red-100">
              This board spent {money(data.data.synthesis_and_model, 4)} on speech
              synthesis or a language model. A soundboard should never touch
              either on a call — the calls will look fine and the margin will
              not.
            </div>
          )}

          <p className="mt-3 text-xs text-ink-500">
            Of {money(data.data.spent, 3)} spent: {money(data.data.recognition, 3)}{' '}
            recognition, {money(data.data.telephony, 3)} telephony. Costs come
            from the rates on the Providers page — a board showing zero has no
            rate set for its speech provider.
          </p>
        </>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-ink-500">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
